/**
 * ScreenGridPanel — reusable grid of scaled V2 screen previews.
 *
 * Composition over the same primitives as ThemeV2PreviewCarousel
 * (V2ScreenView + DeviceMockup + AutoScaleScreenContainer + FontConfigProvider),
 * factored out so multiple surfaces can reuse it:
 *  - the screen picker popup (click-to-add a template to the canvas), and
 *  - any "show theme screens" view.
 *
 * Features: a localStorage-persisted scale slider, an optional auto-focused
 * search field, optional click-to-select/add, and an optional grouping seam
 * (see screenGroups.ts — category today, tag-driven later).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  InputAdornment,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { V2ScreenView, AutoScaleScreenContainer } from '@modules/screen-view-v2';
import { DeviceMockup } from '@modules/device-mockup';
import {
  FontConfigProvider,
  buildFallbackChains,
  collectActiveWebFonts,
  detectPlatform,
  getStudioWebFontsEnv,
  useFontConfig,
  type PlatformId,
} from '@modules/font-config';
import type { StepNode } from '@segmently/schema-models/v2';
import { usePersistentNumber } from '../hooks/usePersistentNumber';
import type { ScreenGrouper, ScreenGroup } from './screenGroups';

// Base (scale=1.0) tile dimensions; mirrors ThemeV2PreviewCarousel's md tile.
const BASE_TILE_WIDTH = 200;
const BASE_TILE_HEIGHT = 420;
const MIN_SCALE = 0.3;
const MAX_SCALE = 1.0;

export interface ScreenGridPanelProps {
  screens: StepNode[];
  locale?: string;
  projectId?: string;
  previewPlatform?: PlatformId;
  /** localStorage key for the persisted scale (required so different surfaces don't share). */
  scaleStorageKey: string;
  defaultScale?: number;
  showSearch?: boolean;
  autoFocusSearch?: boolean;
  searchPlaceholder?: string;
  /** When provided, screens are rendered in collapsible labeled sections. */
  groupBy?: ScreenGrouper;
  /** Click-to-select / click-to-add. */
  onScreenClick?: (screen: StepNode) => void;
  /** Entity/step ids to highlight as selected. */
  selectedKeys?: string[];
  /**
   * Opt-in: per-tile "clone into theme as a new subtype" action. When provided,
   * each tile shows a small "+ subtype" affordance that calls this with the
   * source template; the host captures the subtype label and persists the clone.
   */
  onCloneAsSubtype?: (screen: StepNode) => void;
}

function formatStepKind(screenType: string): string {
  return screenType.replace(/([A-Z])/g, ' $1').trim();
}

function screenId(screen: StepNode): string {
  const c = screen as unknown as { entityId?: string; id?: string };
  return c.entityId ?? c.id ?? '';
}

function screenName(screen: StepNode): string {
  const c = screen as unknown as { name?: string };
  return c.name ?? formatStepKind(screen.screenType as string);
}

/** Single phone tile. */
const ScreenTile: React.FC<{
  screen: StepNode;
  scale: number;
  locale: string;
  selected: boolean;
  onClick?: (screen: StepNode) => void;
  onCloneAsSubtype?: (screen: StepNode) => void;
}> = ({ screen, scale, locale, selected, onClick, onCloneAsSubtype }) => {
  const id = screenId(screen);
  const subtype = (screen as unknown as { subtype?: string }).subtype?.trim();
  return (
    <Box
      data-testid={`screen-grid-tile-${id}`}
      data-screen-grid-tile=""
      data-screen-type={screen.screenType}
      data-screen-subtype={subtype || undefined}
      onClick={onClick ? () => onClick(screen) : undefined}
      sx={{
        position: 'relative',
        cursor: onClick ? 'pointer' : 'default',
        borderRadius: 2,
        p: 1,
        border: '2px solid',
        borderColor: selected ? 'primary.main' : 'transparent',
        transition: 'all 0.15s ease',
        '&:hover': onClick ? { transform: 'scale(1.03)', boxShadow: 3 } : undefined,
      }}
    >
      {onCloneAsSubtype && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon fontSize="small" />}
          data-testid={`screen-grid-tile-clone-${id}`}
          onClick={(event) => {
            event.stopPropagation();
            onCloneAsSubtype(screen);
          }}
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            zIndex: 2,
            minWidth: 'auto',
            px: 0.75,
            py: 0.25,
            fontSize: '0.65rem',
            textTransform: 'none',
            bgcolor: 'background.paper',
          }}
        >
          Subtype
        </Button>
      )}
      <Box
        sx={{
          width: Math.round(BASE_TILE_WIDTH * scale),
          height: Math.round(BASE_TILE_HEIGHT * scale),
          flexShrink: 0,
        }}
      >
        <DeviceMockup device="iphone" autoScale scaleOptions={{ maxScale: 0.5, padding: 4 }}>
          <AutoScaleScreenContainer>
            <V2ScreenView step={screen} locale={locale} previewMode="edit" />
          </AutoScaleScreenContainer>
        </DeviceMockup>
      </Box>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          textAlign: 'center',
          mt: 0.5,
          color: 'text.secondary',
          maxWidth: Math.round(BASE_TILE_WIDTH * scale),
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {screenName(screen)}
      </Typography>
      {subtype && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.25 }}>
          <Chip
            size="small"
            label={subtype}
            data-testid={`screen-grid-tile-subtype-${id}`}
            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }}
          />
        </Box>
      )}
    </Box>
  );
};

const TileFlow: React.FC<{
  screens: StepNode[];
  scale: number;
  locale: string;
  selectedKeys?: string[];
  onScreenClick?: (screen: StepNode) => void;
  onCloneAsSubtype?: (screen: StepNode) => void;
}> = ({ screens, scale, locale, selectedKeys, onScreenClick, onCloneAsSubtype }) => {
  const selected = useMemo(() => new Set(selectedKeys ?? []), [selectedKeys]);
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'flex-start' }}>
      {screens.map((screen) => (
        <ScreenTile
          key={screenId(screen)}
          screen={screen}
          scale={scale}
          locale={locale}
          selected={selected.has(screenId(screen))}
          onClick={onScreenClick}
          onCloneAsSubtype={onCloneAsSubtype}
        />
      ))}
    </Box>
  );
};

/** One collapsible group section (persisted expand state). */
const GroupSection: React.FC<{
  group: ScreenGroup;
  screens: StepNode[];
  scale: number;
  locale: string;
  selectedKeys?: string[];
  onScreenClick?: (screen: StepNode) => void;
  onCloneAsSubtype?: (screen: StepNode) => void;
}> = ({ group, screens, scale, locale, selectedKeys, onScreenClick, onCloneAsSubtype }) => {
  const [expanded, setExpanded] = useState(true);
  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      data-testid={`screen-grid-group-${group.key}`}
      sx={{ bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2">
          {group.label}{' '}
          <Typography component="span" variant="caption" color="text.secondary">
            ({screens.length})
          </Typography>
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1 }}>
        <TileFlow
          screens={screens}
          scale={scale}
          locale={locale}
          selectedKeys={selectedKeys}
          onScreenClick={onScreenClick}
          onCloneAsSubtype={onCloneAsSubtype}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export const ScreenGridPanel: React.FC<ScreenGridPanelProps> = ({
  screens,
  locale = 'en-US',
  projectId,
  previewPlatform,
  scaleStorageKey,
  defaultScale = 0.5,
  showSearch = false,
  autoFocusSearch = false,
  searchPlaceholder = 'Search screens',
  groupBy,
  onScreenClick,
  selectedKeys,
  onCloneAsSubtype,
}) => {
  const { config: fontConfig } = useFontConfig(projectId);
  const platform = useMemo(
    () => previewPlatform ?? detectPlatform(typeof navigator !== 'undefined' ? navigator.userAgent : ''),
    [previewPlatform],
  );
  const fontOverrides = useMemo(() => buildFallbackChains(fontConfig ?? null, platform), [fontConfig, platform]);
  const webFonts = useMemo(
    () => collectActiveWebFonts(fontConfig ?? null, {
      platforms: [platform],
      env: getStudioWebFontsEnv(),
    }),
    [fontConfig, platform],
  );

  const [scale, setScale] = usePersistentNumber(scaleStorageKey, defaultScale, {
    min: MIN_SCALE,
    max: MAX_SCALE,
  });

  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (showSearch && autoFocusSearch) {
      // Focus after the dialog enter-transition (~225ms) so the MUI focus trap
      // doesn't immediately steal focus back; `autoFocus` on the field is the
      // primary mechanism, this is a deterministic backup.
      const t = setTimeout(() => searchRef.current?.focus(), 260);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [showSearch, autoFocusSearch]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return screens;
    return screens.filter((s) => {
      const name = screenName(s).toLowerCase();
      const kind = formatStepKind(s.screenType as string).toLowerCase();
      return name.includes(q) || kind.includes(q);
    });
  }, [screens, search]);

  // Partition into ordered groups (first-seen order) when grouping is enabled.
  const grouped = useMemo(() => {
    if (!groupBy) return null;
    const order: string[] = [];
    const byKey = new Map<string, { group: ScreenGroup; screens: StepNode[] }>();
    for (const screen of filtered) {
      const g = groupBy(screen);
      if (!g) continue;
      if (!byKey.has(g.key)) {
        byKey.set(g.key, { group: g, screens: [] });
        order.push(g.key);
      }
      byKey.get(g.key)!.screens.push(screen);
    }
    return order.map((k) => byKey.get(k)!);
  }, [filtered, groupBy]);

  return (
    <FontConfigProvider fontOverrides={fontOverrides} platform={platform} webFonts={webFonts}>
      <Stack spacing={1.5} data-testid="screen-grid-panel" sx={{ height: '100%', minHeight: 0 }}>
        {/* Controls: search + scale slider */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexShrink: 0 }}>
          {showSearch && (
            <TextField
              inputRef={searchRef}
              autoFocus={autoFocusSearch}
              size="small"
              fullWidth
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="screen-grid-search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 160 }}>
            <Typography variant="caption" color="text.secondary">
              Size
            </Typography>
            <Slider
              size="small"
              value={scale}
              min={MIN_SCALE}
              max={MAX_SCALE}
              step={0.05}
              onChange={(_, value) => setScale(Array.isArray(value) ? value[0] : value)}
              data-testid="screen-grid-scale"
              aria-label="Preview size"
            />
          </Stack>
        </Stack>

        {/* Grid */}
        <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0, pr: 0.5 }}>
          {filtered.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No screens match “{search}”.
            </Typography>
          ) : grouped ? (
            grouped.map(({ group, screens: groupScreens }) => (
              <GroupSection
                key={group.key}
                group={group}
                screens={groupScreens}
                scale={scale}
                locale={locale}
                selectedKeys={selectedKeys}
                onScreenClick={onScreenClick}
                onCloneAsSubtype={onCloneAsSubtype}
              />
            ))
          ) : (
            <TileFlow
              screens={filtered}
              scale={scale}
              locale={locale}
              selectedKeys={selectedKeys}
              onScreenClick={onScreenClick}
              onCloneAsSubtype={onCloneAsSubtype}
            />
          )}
        </Box>
      </Stack>
    </FontConfigProvider>
  );
};

export default ScreenGridPanel;
